/**
 * Phase 3.5 companion widgets for AutoCaptions.
 * Kept in a separate module so the visual-edits babel plugin doesn't
 * choke on the complex SVG + array-of-objects JSX.
 */
import React, { useMemo, useRef } from "react";

/**
 * Mini waveform strip with beat markers.
 * Props:
 *   peaks         — number[] (0..1) sampled RMS values
 *   beats         — number[] seconds
 *   duration      — number seconds (total audio window)
 *   onSeek        — (seconds) => void
 */
export function BeatWaveform({ peaks = [], beats = [], duration = 0, onSeek }) {
  const w = 600;
  const h = 56;
  const barW = peaks.length ? w / peaks.length : 1;
  const beatLines = useMemo(() => {
    if (!duration) return [];
    return beats
      .filter((t) => t <= duration)
      .map((t) => (t / duration) * w);
  }, [beats, duration, w]);

  const handleClick = (e) => {
    if (!onSeek || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    onSeek((px / rect.width) * duration);
  };

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: 56, cursor: onSeek ? "pointer" : "default", display: "block" }}
      onClick={handleClick}
      data-testid="ac-beat-waveform"
    >
      <rect x="0" y="0" width={w} height={h} fill="rgba(0,0,0,0.25)" />
      {peaks.map((v, i) => {
        const bh = Math.max(2, v * (h - 10));
        return (
          <rect
            key={i}
            x={i * barW}
            y={(h - bh) / 2}
            width={Math.max(1, barW - 0.5)}
            height={bh}
            fill="rgba(167, 139, 250, 0.55)"
          />
        );
      })}
      {beatLines.map((x, i) => (
        <line
          key={`b-${i}`}
          x1={x}
          x2={x}
          y1="4"
          y2={h - 4}
          stroke="#facc15"
          strokeWidth="1.5"
          opacity={i % 4 === 0 ? 1 : 0.45}
          data-testid={`ac-beat-marker-${i}`}
        />
      ))}
    </svg>
  );
}

/**
 * Keyframe editor for a single cue.
 * Lets the user drop ≤5 keyframes on a 0-1 timeline and tweak
 * scale (0.5-2.0) + opacity (0-1) per keyframe.
 *
 * Props:
 *   keyframes  — array of {t_ms, scale, opacity}
 *   durationMs — cue duration (ms) for scaling the timeline
 *   onChange   — (next) => void
 */
export function KeyframeEditor({ keyframes = [], durationMs = 2000, onChange }) {
  const timelineRef = useRef(null);

  const update = (next) => {
    const sorted = [...next].sort((a, b) => a.t_ms - b.t_ms);
    onChange?.(sorted);
  };

  const addKeyframe = () => {
    if (keyframes.length >= 5) return;
    const t_ms = Math.round(durationMs * 0.5);
    update([...keyframes, { t_ms, scale: 1.0, opacity: 1.0 }]);
  };

  const removeKeyframe = (idx) => update(keyframes.filter((_, i) => i !== idx));

  const patch = (idx, k, v) => {
    const next = keyframes.map((kf, i) => (i === idx ? { ...kf, [k]: v } : kf));
    update(next);
  };

  return (
    <div className="ac-kf" data-testid="ac-keyframe-editor">
      <div className="ac-kf-header">
        <strong>Keyframes ({keyframes.length}/5)</strong>
        <button
          type="button"
          className="ac-btn ac-btn--ghost ac-btn--sm"
          onClick={addKeyframe}
          disabled={keyframes.length >= 5}
          data-testid="ac-kf-add"
        >
          + Add keyframe
        </button>
      </div>

      <div
        ref={timelineRef}
        className="ac-kf-timeline"
        data-testid="ac-kf-timeline"
      >
        <div className="ac-kf-rule" />
        {keyframes.map((kf, i) => {
          const pct = (kf.t_ms / Math.max(1, durationMs)) * 100;
          return (
            <div
              key={i}
              className="ac-kf-dot"
              style={{ left: `${pct}%` }}
              title={`${kf.t_ms} ms · scale ${kf.scale} · opacity ${kf.opacity}`}
              data-testid={`ac-kf-dot-${i}`}
            >
              <span />
            </div>
          );
        })}
      </div>

      {keyframes.length > 0 && (
        <div className="ac-kf-rows">
          {keyframes.map((kf, i) => (
            <div className="ac-kf-row" key={i}>
              <label>
                <span className="ac-kf-l">T (ms)</span>
                <input
                  type="number"
                  min="0"
                  max={durationMs}
                  step="20"
                  value={kf.t_ms}
                  onChange={(e) => patch(i, "t_ms", parseInt(e.target.value, 10) || 0)}
                  data-testid={`ac-kf-time-${i}`}
                />
              </label>
              <label>
                <span className="ac-kf-l">Scale</span>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.05"
                  value={kf.scale}
                  onChange={(e) => patch(i, "scale", parseFloat(e.target.value))}
                  data-testid={`ac-kf-scale-${i}`}
                />
                <span className="ac-kf-v">{kf.scale.toFixed(2)}×</span>
              </label>
              <label>
                <span className="ac-kf-l">Opacity</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={kf.opacity}
                  onChange={(e) => patch(i, "opacity", parseFloat(e.target.value))}
                  data-testid={`ac-kf-opacity-${i}`}
                />
                <span className="ac-kf-v">{Math.round(kf.opacity * 100)}%</span>
              </label>
              <button
                type="button"
                className="ac-btn ac-btn--ghost ac-btn--sm"
                onClick={() => removeKeyframe(i)}
                data-testid={`ac-kf-del-${i}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {keyframes.length === 0 && (
        <p className="ac-kf-empty">
          Drop up to 5 keyframes on the timeline — tweak scale + opacity per point,
          applies to the first cue (preview only in Advanced render).
        </p>
      )}
    </div>
  );
}
